/*
 * OfferResearchCard — "I can scan this URL" offer.
 *
 * Agent calls `render(directive_type='offer_research', payload={url,
 * label})` when it notices a URL in the conversation but isn't sure
 * if the user wants it scanned. The card lets the user accept (fires
 * a scan_url-equivalent flow) or dismiss.
 *
 * Brand: brand-50 surface, hairline ring, brand-blue primary button.
 */

import React from 'react';
import { Link as LinkIcon, X } from 'lucide-react';

import type { DirectivePayload } from '../../lib/dossier-types';


export interface OfferResearchCardProps {
  payload: DirectivePayload;
  /** Fires with the URL when the user accepts. The chat handler then
   *  runs the same code path as a pasted URL (classify_url + fetch_url
   *  on the backend, scan_url for the legacy autoscan chip). */
  onScan: (url: string) => void;
  /** Optional decline handler — when omitted the dismiss button is
   *  hidden (some offers are non-dismissible by design). */
  onDecline?: () => void;
  disabled?: boolean;
}


function shortenUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}


export const OfferResearchCard: React.FC<OfferResearchCardProps> = ({
  payload, onScan, onDecline, disabled,
}) => {
  const url = payload.url || '';
  const label = payload.label || `Scan ${shortenUrl(url)}`;

  if (!url) return null;

  return (
    <div className="aa-directive aa-directive--offer">
      <div className="aa-directive-url-row">
        <LinkIcon size={13} className="aa-directive-url-ico" strokeWidth={2.25} />
        <code className="aa-directive-url">{shortenUrl(url)}</code>
      </div>
      <div className="aa-directive-actions">
        <button
          type="button"
          className="aa-directive-btn aa-directive-btn--primary"
          disabled={disabled}
          onClick={() => onScan(url)}
        >
          <span>{label}</span>
        </button>
        {onDecline && (
          <button
            type="button"
            className="aa-directive-btn aa-directive-btn--ghost"
            disabled={disabled}
            onClick={onDecline}
            aria-label="Decline"
          >
            <X size={13} strokeWidth={2.5} />
            <span>Not that one</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OfferResearchCard;
